import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from '@/store/authStore';

// Pages
import SplashPage from '@/pages/SplashPage';
import LoginPage from '@/pages/auth/LoginPage';
import OTPLoginPage from '@/pages/auth/OTPLoginPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import DashboardPage from '@/pages/DashboardPage';
import ProfilePage from '@/pages/ProfilePage';
import BenefitsPage from '@/pages/BenefitsPage';
import ClaimsListPage from '@/pages/claims/ClaimsListPage';
import CreateClaimPage from '@/pages/claims/CreateClaimPage';
import DigitalCardPage from '@/pages/card/DigitalCardPage';
import OfflineCardPage from '@/pages/card/OfflineCardPage';
import ProviderSearchPage from '@/pages/providers/ProviderSearchPage';
import ProviderListPage from '@/pages/providers/ProviderListPage';
import NotificationCenterPage from '@/pages/notifications/NotificationCenterPage';
import SupportListPage from '@/pages/support/SupportListPage';
import NewTicketPage from '@/pages/support/NewTicketPage';
import SettingsPage from '@/pages/SettingsPage';
import PinSetupPage from '@/pages/auth/PinSetupPage';

// Components
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ProtectedRoute from '@/components/ProtectedRoute';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import InstallPromptBanner from '@/components/pwa/InstallPromptBanner';

function App() {
  const { isLoading, isAuthenticated } = useAuthStore();

  if (isLoading) {
    return (
      <div className="mobile-container">
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="mobile-container">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/splash" element={<SplashPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/otp" element={<OTPLoginPage />} />
          <Route path="/auth/pin-setup" element={<PinSetupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          {/* Profile */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          {/* Benefits */}
          <Route
            path="/benefits"
            element={
              <ProtectedRoute>
                <BenefitsPage />
              </ProtectedRoute>
            }
          />
          {/* Claims */}
          <Route
            path="/claims"
            element={
              <ProtectedRoute>
                <ClaimsListPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/claims/new"
            element={
              <ProtectedRoute>
                <CreateClaimPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/claims/:id"
            element={
              <ProtectedRoute>
                <ClaimsListPage />
              </ProtectedRoute>
            }
          />

          {/* Digital Card */}
          <Route
            path="/card"
            element={
              <ProtectedRoute>
                <DigitalCardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/card/offline"
            element={
              <ProtectedRoute>
                <OfflineCardPage />
              </ProtectedRoute>
            }
          />

          {/* Providers */}
          <Route
            path="/providers"
            element={
              <ProtectedRoute>
                <ProviderSearchPage />
              </ProtectedRoute>
            }
          />
           <Route
            path="/providers/list"
            element={
              <ProtectedRoute>
                <ProviderListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/providers/:id"
            element={
              <ProtectedRoute>
                <ProviderListPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationCenterPage />
              </ProtectedRoute>
            }
          />

          {/* Support */}
           <Route
            path="/support"
            element={
              <ProtectedRoute>
                <SupportListPage />
              </ProtectedRoute>
            }
          />
           <Route
            path="/support/new"
            element={
              <ProtectedRoute>
                <NewTicketPage />
              </ProtectedRoute>
            }
          />
           <Route
            path="/support/tickets/:id"
            element={
              <ProtectedRoute>
                <SupportListPage />
              </ProtectedRoute>
            }
          />

          {/* 404 Route */}
          <Route
            path="*"
            element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />}
          />
        </Routes>

        {isAuthenticated && <InstallPromptBanner />}

        {/* Toast Notifications */}
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
              padding: '16px',
              borderRadius: '8px',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </div>
    </ErrorBoundary>
  );
}

export default App;